import { doc, collection, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Direct Firestore meetings CRUD — the reconcile callable is a different
// scenario. canEditMeetingFor get()s the parent student, so a missing student
// always denies. The owning-CM path can only be probed when this role can
// also create that student. Plain case_manager / speech_case_manager cannot,
// so that positive path is SKIPPED rather than dressed up as a correct denial.
//
// The security-interesting negatives still run when we can create students:
// admins listed as CM stay denied; sped chairs do not get school-wide IEP
// write; admin_504 gets every 504 student.

const MEETING_CM_ROLES_WITHOUT_CREATE = ['case_manager', 'speech_case_manager']

function studentPayload(ctx, { caseManagerId, plan, suffix }) {
  return {
    app: {
      studentData: {
        firstName: 'SMOKE',
        lastName: `Mtg ${ctx.runId.slice(-8)}`,
        plan,
        grade: '9',
        ssid: `SMOKE-MTG-${ctx.runId}-${suffix}`,
        caseManagerId
      },
      accommodations: { instruction: '', assessment: '' },
      schedule: { classServices: [] },
      staffIds: []
    },
    __smokeTest: true,
    __smokeRunId: ctx.runId,
    createdAt: serverTimestamp(),
    ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
  }
}

function meetingPayload(ctx, studentId) {
  return {
    studentId,
    date: '2026-09-15',
    type: 'IEP',
    status: 'hold',
    __smokeTest: true,
    __smokeRunId: ctx.runId,
    createdAt: serverTimestamp(),
    ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
  }
}

function expectMeetingWrite(ctx, { studentExists, caseManagerId, plan }) {
  return RULE_EXPECTATIONS.meetingWrite(ctx.roleClaim, {
    studentExists,
    uid: ctx.user.uid,
    caseManagerId,
    plan
  })
}

export default {
  id: 'meetingsCrud',
  label: 'Meeting document create/delete',
  description: 'Direct meetings collection writes for your role. Does not call reconcileMeetings.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'meeting document write',
        'Select a school before probing school-scoped meeting documents'
      )
      return
    }

    const canCreateStudent = RULE_EXPECTATIONS.studentCreate(role)
    const ownRef = doc(collection(db, 'students'))
    const otherIepRef = doc(collection(db, 'students'))
    const other504Ref = doc(collection(db, 'students'))
    const meetingRefs = []
    let ownCreated = false
    let otherIepCreated = false
    let other504Created = false

    const writeMeeting = async (label, studentId, studentExists, caseManagerId, plan) => {
      const expectWrite = expectMeetingWrite(ctx, { studentExists, caseManagerId, plan })
      const meetingRef = doc(collection(db, 'meetings'))
      meetingRefs.push(meetingRef)
      let created = false
      await step(`${label} (expected: ${fmtExpected(expectWrite)})`, async () => {
        const { allowed } = await attempt(() => setDoc(meetingRef, meetingPayload(ctx, studentId)))
        created = allowed
        return allowed
          ? `${accessDetail(expectWrite, allowed)} — meetings/${meetingRef.id}`
          : accessDetail(expectWrite, allowed)
      })
      return { meetingRef, created, expectWrite }
    }

    try {
      if (canCreateStudent) {
        await step('create own-CM IEP student (expected: allowed)', async () => {
          const { allowed } = await attempt(() =>
            setDoc(
              ownRef,
              studentPayload(ctx, { caseManagerId: ctx.user.uid, plan: 'IEP', suffix: 'own' })
            )
          )
          ownCreated = allowed
          return allowed
            ? `allowed, as expected — students/${ownRef.id}`
            : accessDetail(true, allowed)
        })
      } else if (MEETING_CM_ROLES_WITHOUT_CREATE.includes(role)) {
        step.skip(
          'owning-case-manager meeting',
          `${role} cannot create a student to own, so the owning-CM meeting path cannot be probed from this account. Use a seeded caseload account. The no-student denial is still asserted below.`
        )
      }

      if (ownCreated) {
        const own = await writeMeeting(
          'write meeting for a student you own',
          ownRef.id,
          true,
          ctx.user.uid,
          'IEP'
        )
        if (!own.created) {
          step.skip('read/delete own meeting', 'Meeting was not written — later steps skipped')
        } else {
          await step('read own meeting back (expected: allowed)', async () => {
            const { allowed } = await attempt(async () => {
              const snap = await getDoc(own.meetingRef)
              if (!snap.exists()) throw new Error('Created meeting not found on read-back')
            })
            return accessDetail(true, allowed)
          })
          await step(`delete own meeting (expected: ${fmtExpected(own.expectWrite)})`, async () => {
            const { allowed } = await attempt(() => deleteDoc(own.meetingRef))
            return accessDetail(own.expectWrite, allowed)
          })
        }
      } else {
        await writeMeeting(
          'write meeting with no student document',
          `smoke-mtg-missing-${ctx.runId.slice(-8)}`,
          false,
          ctx.user.uid,
          'IEP'
        )
      }

      if (canCreateStudent) {
        await step('create other-CM IEP student (expected: allowed)', async () => {
          const { allowed } = await attempt(() =>
            setDoc(
              otherIepRef,
              studentPayload(ctx, {
                caseManagerId: 'smoke-other-cm',
                plan: 'IEP',
                suffix: 'iep'
              })
            )
          )
          otherIepCreated = allowed
          return allowed
            ? `allowed, as expected — students/${otherIepRef.id}`
            : accessDetail(true, allowed)
        })
        if (otherIepCreated) {
          await writeMeeting(
            'write meeting for other-CM IEP student',
            otherIepRef.id,
            true,
            'smoke-other-cm',
            'IEP'
          )
        } else {
          step.skip('other-CM IEP meeting', 'Other-CM IEP student was not created')
        }

        await step('create other-CM 504 student (expected: allowed)', async () => {
          const { allowed } = await attempt(() =>
            setDoc(
              other504Ref,
              studentPayload(ctx, {
                caseManagerId: 'smoke-other-cm',
                plan: '504',
                suffix: '504'
              })
            )
          )
          other504Created = allowed
          return allowed
            ? `allowed, as expected — students/${other504Ref.id}`
            : accessDetail(true, allowed)
        })
        if (other504Created) {
          const other504 = await writeMeeting(
            'write meeting for other-CM 504 student',
            other504Ref.id,
            true,
            'smoke-other-cm',
            '504'
          )
          if (other504.created) {
            await deleteDoc(other504.meetingRef).catch(() => {})
          }
        } else {
          step.skip('other-CM 504 meeting', 'Other-CM 504 student was not created')
        }
      }
    } finally {
      for (const ref of meetingRefs) {
        await deleteDoc(ref).catch(() => {})
      }
      if (ownCreated) await deleteDoc(ownRef).catch(() => {})
      if (otherIepCreated) await deleteDoc(otherIepRef).catch(() => {})
      if (other504Created) await deleteDoc(other504Ref).catch(() => {})
    }
  }
}
